import {
  Button,
  Dropdown,
  Option,
  OptionOnSelectData,
  Spinner,
} from "@fluentui/react-components";
import Container from "../../components/Container";
import Typography from "../../components/Typography";
import { customTokens } from "../../theme/theme";
import {
  ChatSparkle20Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import { useGlobalSearchStyles } from "./GlobalSearch.styles";
import { FC, Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { toggleAssistantWindow } from "../../store/slices/ClimateAssistantSlice";
import { SearchResult } from "../../layout";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../constants/routes";
import { setOpenSearchPage } from "../../store/slices/GlobalSearchSlice";

interface IGlobalSearch {
  triggerSearchApi: any;
  searchResults: SearchResult;
  countryList: any[];
  searchLoading: boolean;
}

const options = [10, 20, 30, 40];
const searchSortOptions = ["Page", "Relevancy"];

const GlobalSearch: FC<IGlobalSearch> = ({
  triggerSearchApi,
  searchResults,
  countryList,
  searchLoading,
}) => {
  const styles = useGlobalSearchStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { searchText } = useSelector((state: RootState) => state.globalSearch);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [selectedOptions, setSelectedOptions] = useState<string[]>(["Page"]);
  const [dropdownValue, setDropdownValue] = useState("Page");

  // local results to control clearing on pagination
  const [localResults, setLocalResults] = useState<SearchResult | null>(null);

  useEffect(() => {
    if (searchResults && searchResults?.pagination) {
      setPagination({
        page: searchResults?.pagination?.page ?? 1,
        pageSize: searchResults?.pagination?.pageSize ?? 10,
        totalPages: searchResults?.pagination?.totalPages ?? 0,
        totalRecords: searchResults?.pagination?.totalRecords ?? 0,
      });
    }
  }, [searchResults]);

  //Sync local results whenever parent results update
  useEffect(() => {
    if (searchResults) {
      setLocalResults(searchResults);
    }
  }, [searchResults]);

  const onOptionSelect = (data: OptionOnSelectData) => {
    const sortValue = data.optionText ?? "Page";
    setSelectedOptions(data.selectedOptions);
    setDropdownValue(sortValue);

    const payload = {
      query: searchText,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sortValue,
    };

    // Clear local results before fetching sorted data
    setLocalResults({ search_Result: [], pagination });
    triggerSearchApi(payload);
  };

  const handlePrev = () => {
    if (pagination.page <= 1) return;
    setPagination((prev) => {
      const payload = {
        query: searchText,
        page: Math.max(1, prev.page - 1),
        pageSize: prev.pageSize,
        sort: dropdownValue,
      };

      // Clear results before loading previous page
      setLocalResults({ search_Result: [], pagination: { ...prev } });
      triggerSearchApi(payload);

      return {
        ...prev,
        page: Math.max(1, prev.page - 1),
      };
    });
  };

  const handleNext = () => {
    if (pagination.page >= pagination.totalPages) return;
    setPagination((prev) => {
      const payload = {
        query: searchText,
        page: Math.min(prev.totalPages, prev.page + 1),
        pageSize: prev.pageSize,
        sort: dropdownValue,
      };

      // Clear results before loading next page
      setLocalResults({ search_Result: [], pagination: { ...prev } });
      triggerSearchApi(payload);

      return {
        ...prev,
        page: Math.min(prev.totalPages, prev.page + 1),
      };
    });
  };

  const decodeHTML = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const highlightMatchSafe = (
    text: string,
    charsPerLineDesktop = 110,
    charsPerLineMobile = 50
  ) => {
    if (!searchText) return [text];

    const cleanText = decodeHTML(text.replace(/<[^>]*>/g, ""));
    const regex = new RegExp(`(${searchText})`, "gi");
    const matchIndex = cleanText
      .toLowerCase()
      .indexOf(searchText.toLowerCase());

    if (matchIndex === -1) return [cleanText];

    const isMobile = window.innerWidth < 600;

    const charsPerLine = isMobile ? charsPerLineMobile : charsPerLineDesktop;

    const minChars = charsPerLine * 2;

    let start = Math.max(0, matchIndex - Math.floor(minChars / 2));
    let end = Math.min(
      cleanText.length,
      matchIndex + searchText.length + Math.floor(minChars / 2)
    );

    if (end - start < minChars) {
      if (start === 0) {
        end = Math.min(cleanText.length, start + minChars);
      } else if (end === cleanText.length) {
        start = Math.max(0, end - minChars);
      } else {
        const extra = minChars - (end - start);
        start = Math.max(0, start - Math.floor(extra / 2));
        end = Math.min(cleanText.length, end + Math.ceil(extra / 2));
      }
    }

    let truncatedText = cleanText.slice(start, end);

    if (start > 0) truncatedText = "..." + truncatedText;
    if (end < cleanText.length) truncatedText = truncatedText + "...";

    const parts = truncatedText.split(regex);

    return (
      <p style={{ margin: 0 }}>
        {parts.map((part, index) =>
          part.toLowerCase() === searchText.toLowerCase() ? (
            <b key={index}>{part}</b>
          ) : (
            part
          )
        )}
      </p>
    );
  };

  const routeCountryPage = (country: string) => {
    const selectedCountry = countryList.find(
      (item: any) => item.name === country
    );

    navigate(`${ROUTES.COUNTRIES}/${selectedCountry.isoCode}/`);
  };

  const handleBreadcrumbs = (pageName: string, route: string) => {
    switch (pageName) {
      case "Dataset":
        navigate(`${ROUTES.DATASETS}${ROUTES.FTC_DATASETS}`);
        dispatch(setOpenSearchPage(false));
        break;

      case "About":
        navigate(ROUTES.ABOUT);
        dispatch(setOpenSearchPage(false));
        break;

      case "Home":
        navigate(ROUTES.HOME);
        dispatch(setOpenSearchPage(false));
        break;

      case "Countries":
        routeCountryPage(route);
        dispatch(setOpenSearchPage(false));
        break;

      default:
        navigate(ROUTES.HOME);
        dispatch(setOpenSearchPage(false));
        break;
    }
  };

  const createBreadcrumbs = (pageName: string, heading: string) => {
    switch (pageName) {
      case "Countries":
        return <>{`Home > ${pageName} > ${heading}`}</>;

      case "Dataset":
        return <>{`Home > ${pageName}s > FTC Support`}</>;

      case "Home":
        return "Home";

      case "About":
        return <>{`Home > ${pageName}`}</>;
    }
  };

  return (
    <Container className={styles.searchContainer}>
      <div className={styles.searchResultBar}>
        <div>
          <Typography
            variant="bodyLSemibold"
            mobileVariant="bodyMSemibold"
            color={customTokens.fontBlack100}
          >
            Results for:{" "}
          </Typography>

          <Typography
            variant="bodyLSemibold"
            mobileVariant="bodyMSemibold"
            color={customTokens.colorBrandForegroundLink}
            className={styles.searchResult}
          >
            {searchText}
          </Typography>
        </div>

        <div className={styles.sortByDropdownWrapper}>
          <label htmlFor="sortBy">
            <Typography
              variant="body1"
              color={customTokens.colorNeutralForeground1}
            >
              Sort by:
            </Typography>
          </label>

          <Dropdown
            id="sortBy"
            style={{ minWidth: "200px" }}
            value={dropdownValue}
            selectedOptions={selectedOptions}
            onOptionSelect={(_, data) => onOptionSelect(data)}
            className={styles.sortByDropdown}
          >
            {searchSortOptions.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Dropdown>
        </div>
      </div>

      <div>
        <Typography variant="body1" color={customTokens.fontBlack100}>
          {localResults?.pagination?.totalRecords} results
        </Typography>
      </div>

      {searchLoading ? (
        <Spinner
          labelPosition="below"
          label="Loading results, please wait..."
        />
      ) : (localResults?.search_Result?.length ?? 0) > 0 ? (
        <div className={styles.searchResultBoxWrapper}>
          {(localResults?.search_Result ?? []).map((item) => {
            return (
              <Fragment key={item.pageName}>
                <div className={styles.resultPageName}>
                  <Typography
                    variant="headingH1"
                    mobileVariant="headingH4Semibold"
                    color={customTokens.fontDarkGrayBody}
                  >
                    {item.pageName}
                  </Typography>
                </div>

                {item.result.map((res) => {
                  return (
                    <div key={res.heading} className={styles.searchResultBox}>
                      <Typography
                        variant="bodyLSemibold"
                        color={customTokens.colorBrandForegroundLink}
                      >
                        {res.heading}
                      </Typography>

                      <Typography
                        variant="body1"
                        color={customTokens.colorBrandForegroundLink}
                        className={styles.breadcrumbs}
                        onClick={() =>
                          handleBreadcrumbs(item.pageName, res.heading)
                        }
                      >
                        {createBreadcrumbs(item.pageName, res.heading)}
                      </Typography>

                      <Typography
                        variant="bodyLRegular"
                        color={customTokens.fontDarkGrayBody}
                      >
                        {highlightMatchSafe(res.text)}
                      </Typography>
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
          <div className={styles.tableFooterRow}>
            <Dropdown
              appearance="underline"
              value={`Show ${pagination.pageSize}`}
              style={{ minWidth: "auto", borderBottom: "none" }}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  setPagination((prev) => {
                    const payload = {
                      query: searchText,
                      page: 1,
                      pageSize: Number(data.optionValue),
                      sort: dropdownValue,
                    };
                    // Clear results before changing page size
                    setLocalResults({
                      search_Result: [],
                      pagination: { ...prev },
                    });
                    triggerSearchApi(payload);

                    return {
                      ...prev,
                      page: 1,
                      pageSize: Number(data.optionValue),
                    };
                  });
                }
              }}
            >
              {options.map((size) => (
                <Option key={size} value={size.toString()}>
                  {`Show ${size}`}
                </Option>
              ))}
            </Dropdown>

            <div className={styles.tablePageControls}>
              <Button
                icon={<ChevronLeft16Regular />}
                appearance="subtle"
                disabled={pagination.page === 1}
                onClick={handlePrev}
              />

              <Typography variant="body2">{`${pagination.page} of ${pagination.totalPages}`}</Typography>

              <Button
                icon={<ChevronRight16Regular />}
                appearance="subtle"
                disabled={pagination.page === pagination.totalPages}
                onClick={handleNext}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.noResultBox}>
          <Typography
            variant="bodyLSemibold"
            color={customTokens.colorNeutralForeground3}
          >
            No results found
          </Typography>

          <Typography
            variant="body2"
            color={customTokens.colorNeutralForeground3}
            className={styles.sorryText}
          >
            Sorry, we can't find any items matching your search. <br />
            Please try again or use{" "}
            <Button
              className={styles.aiButton}
              icon={
                <ChatSparkle20Regular
                  color={customTokens.colorBrandBackground}
                />
              }
              onClick={() => dispatch(toggleAssistantWindow(true))}
            >
              AI Climate Assistant
            </Button>{" "}
            for a detailed search.
          </Typography>
        </div>
      )}
    </Container>
  );
};

export default GlobalSearch;
